import { useState } from 'react';
import styled from 'styled-components';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';

type Choice = { value: string; label: string; disabled?: boolean };

export default function LiveChoiceButtons({ label, value, options, onChange, disabled = false, compact = false, hideLabel = false, searchable = false }: {
  label: string; value: string; options: Choice[]; onChange: (value: string) => void;
  disabled?: boolean; compact?: boolean; hideLabel?: boolean; searchable?: boolean;
}) {
  const [search, setSearch] = useState('');
  const filtered = searchable ? options.filter(option => option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())) : options;
  return <Choices aria-label={label} disabled={disabled} $compact={compact}>
    <legend hidden={hideLabel}>{label}</legend>
    {searchable && options.length > 8 && <input type="search" aria-label={`${label} 검색`} placeholder={`${label} 검색`}
      value={search} onChange={event => setSearch(event.target.value)} />}
    <div className="choices">{filtered.map(option => <AdminActionButton key={option.value} type="button"
      aria-pressed={value === option.value} disabled={disabled || option.disabled} onClick={() => { if (value !== option.value) onChange(option.value); }}>
      {option.label}
    </AdminActionButton>)}</div>
    {filtered.length === 0 && <p>선택할 항목이 없습니다.</p>}
  </Choices>;
}

const Choices = styled.fieldset<{ $compact: boolean }>`
  box-sizing: border-box; min-width: 0; margin: 0; padding: 0; border: 0;
  color: var(--color-text-primary); font-size: var(--text-body);
  legend { padding: 0; margin-bottom: var(--space-2); font-weight: var(--font-weight-semibold); }
  legend[hidden] { display: none; }
  > input { box-sizing: border-box; width: 100%; min-width: 0; min-height: var(--control-lg); margin-bottom: var(--space-2);
    padding: 0 var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
    color: var(--color-text-primary); background: var(--color-bg-surface); font: inherit; }
  > input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  .choices { display: flex; flex-wrap: wrap; gap: var(--space-2); max-height: calc(var(--control-lg) * 5); overflow-y: auto; padding: var(--space-1); }
  && .choices > button {
    flex: 0 1 auto; min-width: var(--touch-target); min-height: ${props => props.$compact ? 'var(--control-sm)' : 'var(--control-lg)'};
    padding: var(--space-1) ${props => props.$compact ? 'var(--space-2)' : 'var(--space-3)'};
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface);
    color: var(--color-text-primary); font: inherit; font-weight: var(--font-weight-medium); white-space: normal; overflow-wrap: anywhere;
  }
  && .choices > button:hover:not(:disabled) { background: var(--color-bg-subtle); border-color: var(--color-border-strong); }
  && .choices > button[aria-pressed='true'] { background: var(--color-brand-subtle); border-color: var(--color-brand); color: var(--color-text-link); }
  && .choices > button:disabled { background: var(--color-bg-muted); border-color: var(--color-border-default); color: var(--color-text-disabled); cursor: not-allowed; }
  && .choices > button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  > p { margin: var(--space-2) 0 0; color: var(--color-text-secondary); }
  @media (max-width: 767px), (pointer: coarse) { && .choices > button { min-height: var(--touch-target); } }
`;
